import * as React from 'react'

import { Pencil, PlusIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { DisabledReasonTooltip } from '~/client/src/shared/components/DisabledReasonTooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { useResource, useResourceActions } from '../context'
import { useSelectionHasLockedRecord } from '../hooks/useSelectionHasLockedRecord'
import { useSelectionHasProjectLocation } from '../hooks/useSelectionHasProjectLocation'

export const ResourceCreateButton: React.FC = () => {
  const { createNew } = useResourceActions()

  return (
    <Button
      variant="default"
      size="lg"
      onClick={createNew}
      data-testid="resource-create-button"
    >
      <PlusIcon data-icon="inline-start" />
      {Localization.translator.create}
    </Button>
  )
}

export const ResourceBulkEditButton: React.FC = observer(() => {
  const {
    meta: { selectedCount },
    actions: { openBulkEditPanel },
  } = useResource()
  // Above the early return: it is the first statement after the destructure, and a hook
  // below it would break hook order on the render where nothing is selected.
  const hasProjectLocationSelected = useSelectionHasProjectLocation()
  const hasLockedRecordSelected = useSelectionHasLockedRecord()

  if (selectedCount === 0) return null

  return (
    <DisabledReasonTooltip
      isDenied={hasProjectLocationSelected || hasLockedRecordSelected}
      reason={
        hasProjectLocationSelected
          ? Localization.translator.projectLocationBulkEditProhibited
          : Localization.translator.selectionHasResourcesYouCannotChange
      }
    >
      <Button
        variant="primary-ghost"
        disabled={hasProjectLocationSelected || hasLockedRecordSelected}
        onClick={openBulkEditPanel}
        data-testid="resource-bulk-edit-button"
      >
        <Pencil />
        {Localization.translator.bulkEdit}
      </Button>
    </DisabledReasonTooltip>
  )
})

interface ResourceImportButtonProps {
  onClick: () => void
}

export const ResourceImportButton: React.FC<ResourceImportButtonProps> = ({
  onClick,
}) => {
  return (
    <Button variant="outline" size="lg" onClick={onClick}>
      {Localization.translator.import}
    </Button>
  )
}

interface ResourceExportButtonProps {
  onClick: () => void
}

export const ResourceExportButton: React.FC<ResourceExportButtonProps> = ({
  onClick,
}) => {
  return (
    <Button variant="outline" size="lg" onClick={onClick}>
      {Localization.translator.export}
    </Button>
  )
}
